/**
 * Parser único del JSON persistido por CapturaPage en `gestiones.notas`.
 * Compartido por useGestionNotas y useCapturaHydrate.
 */

export interface GestionNotasContrato {
  modalidad_pago: string | null;
  fecha_firma_est: string | null;
  situacion_legal: string | null;
  colindancias: {
    norte: string | null;
    sur: string | null;
    oriente: string | null;
    poniente: string | null;
  };
  condiciones: string | null;
}

export interface GestionNotasGestion {
  postura_propietario: string | null;
  motivo_inviable: string | null;
}

export interface GestionNotasCampo {
  es_el_propietario: 'si' | 'no' | null;
  signatario_nombre: string | null;
  tercero_relacion: string | null;
  calidad_posesionario: string | null;
  relacion_ejido: string | null;
  liderazgo_ejido: string | null;
  acta_nacimiento_folio: string | null;
}

export interface GestionNotasNarrativa {
  descripcion_visita: string | null;
  descripcion_negociacion: string | null;
  reqs_area_legal: string | null;
  observaciones_tecnico: string | null;
}

export interface GestionNotasParsed {
  /** Existe una row de gestión para esta parcela. */
  has_gestion: boolean;
  /** Notas raw como texto plano (cuando NO es JSON parseable). Null si era JSON OK o estaba vacía. */
  notas_text: string | null;
  /** Bloques estructurados — cada campo es null si la subllave no existía. */
  contrato: GestionNotasContrato | null;
  gestion: GestionNotasGestion | null;
  campo: GestionNotasCampo | null;
  /** Narrativa paso 2 captura embebida en el JSON de notas (v2026-05+). */
  narrativa: GestionNotasNarrativa | null;
  /** Otros datos directos de la columna `gestiones`, no del JSON. */
  riesgo: 'bajo' | 'medio' | 'alto' | null;
  estado: string | null;
  firmado: boolean;
  plazo_anos: number | null;
  monto_renta_ha: number | null;
  /** Bono firma + construcción (parseados del JSON) — útiles si el cliente quiere ver términos económicos crudos. */
  bono_firma_mxn: number | null;
  construccion: {
    plazo_anos: number | null;
    tarifa_mxn_unidad: number | null;
    cantidad_unidades: number | null;
    subtotal_mxn: number | null;
  } | null;
  modelo_negocio_total_mxn: number | null;
}

function asString(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t === '' ? null : t;
}

function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export interface ParsedGestionNotasBlocks {
  contrato: GestionNotasContrato | null;
  gestion: GestionNotasGestion | null;
  campo: GestionNotasCampo | null;
  narrativa: GestionNotasNarrativa | null;
  bono_firma_mxn: number | null;
  construccion: GestionNotasParsed['construccion'];
  modelo_negocio_total_mxn: number | null;
  fallbackText: string | null;
}

/**
 * Extrae los bloques estructurados de la columna `gestiones.notas`.
 * Si el valor no es JSON de objeto: `fallbackText` = texto crudo, resto null.
 */
export function parseGestionNotasFromColumn(raw: string | null | undefined): ParsedGestionNotasBlocks {
  if (raw == null || raw.trim() === '') {
    return {
      contrato: null,
      gestion: null,
      campo: null,
      narrativa: null,
      bono_firma_mxn: null,
      construccion: null,
      modelo_negocio_total_mxn: null,
      fallbackText: null,
    };
  }
  const notas = raw.trim();
  let obj: Record<string, unknown> | null = null;
  try {
    const parsed = JSON.parse(notas);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      obj = parsed as Record<string, unknown>;
    }
  } catch {
    /* legacy plain text */
  }

  if (!obj) {
    return {
      contrato: null,
      gestion: null,
      campo: null,
      narrativa: null,
      bono_firma_mxn: null,
      construccion: null,
      modelo_negocio_total_mxn: null,
      fallbackText: notas,
    };
  }

  const c =
    obj.contrato && typeof obj.contrato === 'object'
      ? (obj.contrato as Record<string, unknown>)
      : null;
  const col =
    c?.colindancias && typeof c.colindancias === 'object'
      ? (c.colindancias as Record<string, unknown>)
      : null;
  const contrato: GestionNotasContrato | null = c
    ? {
        modalidad_pago: asString(c.modalidad_pago),
        fecha_firma_est: asString(c.fecha_firma_est),
        situacion_legal: asString(c.situacion_legal),
        colindancias: {
          norte: asString(col?.norte),
          sur: asString(col?.sur),
          oriente: asString(col?.oriente),
          poniente: asString(col?.poniente),
        },
        condiciones: asString(c.condiciones),
      }
    : null;

  const g =
    obj.gestion && typeof obj.gestion === 'object'
      ? (obj.gestion as Record<string, unknown>)
      : null;
  const gestion: GestionNotasGestion | null = g
    ? {
        postura_propietario: asString(g.postura_propietario),
        motivo_inviable: asString(g.motivo_inviable),
      }
    : null;

  const cmp =
    obj.campo && typeof obj.campo === 'object' ? (obj.campo as Record<string, unknown>) : null;
  const campo: GestionNotasCampo | null = cmp
    ? {
        es_el_propietario:
          cmp.es_el_propietario === 'si' || cmp.es_el_propietario === 'no'
            ? cmp.es_el_propietario
            : null,
        signatario_nombre: asString(cmp.signatario_nombre),
        tercero_relacion: asString(cmp.tercero_relacion),
        calidad_posesionario: asString(cmp.calidad_posesionario),
        relacion_ejido: asString(cmp.relacion_ejido),
        liderazgo_ejido: asString(cmp.liderazgo_ejido),
        acta_nacimiento_folio: asString(cmp.acta_nacimiento_folio),
      }
    : null;

  const nar =
    obj.narrativa && typeof obj.narrativa === 'object'
      ? (obj.narrativa as Record<string, unknown>)
      : null;
  const narrativa: GestionNotasNarrativa | null = nar
    ? {
        descripcion_visita: asString(nar.descripcion_visita),
        descripcion_negociacion: asString(nar.descripcion_negociacion),
        reqs_area_legal: asString(nar.reqs_area_legal),
        observaciones_tecnico: asString(nar.observaciones_tecnico),
      }
    : null;

  const con =
    obj.construccion && typeof obj.construccion === 'object'
      ? (obj.construccion as Record<string, unknown>)
      : null;
  const construccion: GestionNotasParsed['construccion'] = con
    ? {
        plazo_anos: asNumber(con.plazo_anos),
        tarifa_mxn_unidad: asNumber(con.tarifa_mxn_unidad),
        cantidad_unidades: asNumber(con.cantidad_unidades),
        subtotal_mxn: asNumber(con.subtotal_mxn),
      }
    : null;

  const allNarrativaNull =
    narrativa == null ||
    (
      narrativa.descripcion_visita == null &&
      narrativa.descripcion_negociacion == null &&
      narrativa.reqs_area_legal == null &&
      narrativa.observaciones_tecnico == null
    );

  return {
    contrato,
    gestion,
    campo,
    narrativa: allNarrativaNull ? null : narrativa,
    construccion,
    bono_firma_mxn: asNumber(obj.bono_firma_mxn),
    modelo_negocio_total_mxn: asNumber(obj.modelo_negocio_total_mxn),
    fallbackText: null,
  };
}
